/**
 * Where the fibre canvas earns its place, and where it is in the way.
 *
 * ## The problem it fixes
 *
 * The animated network was drawn behind every route at full width. On a
 * dashboard that is the subject of the screen. On a Kanban board or a
 * fifty-row stock table it is motion behind text, and the eye keeps tracking
 * it — reading as an unfinished background rather than as the network.
 *
 * The same reasoning retired it from the 256px rail: the wrong surface for it
 * is the one where something else is being read.
 *
 * ## Why a list rather than a prop on each page
 *
 * Because the decision is editorial and belongs in one place where it can be
 * read as a policy and argued with. A prop threaded through forty pages is
 * forty chances for a new screen to inherit the wrong default silently — and
 * the default that matters here is the quiet one.
 *
 * New routes therefore get `tint` unless they are listed. A page that should
 * have the full canvas says so; a page that forgot simply stays calm.
 */
export type CanvasSurface = 'full' | 'tint';

/**
 * The screens where the network is the subject, or where there is nothing to
 * read over it.
 *
 * Matched as prefixes so `/network/design` follows `/network`, and kept short
 * on purpose: this list growing is the thing to notice.
 */
const FULL_CANVAS = [
  '/dashboard',
  '/network',
  '/whats-new',
  '/help',
  '/awaiting-approval',
  '/awaiting-activation',
  '/access-refused',
] as const;

/**
 * The root is the sign-in screen when signed out and a landing when signed in.
 * Both are the canvas's own territory — there is no table on either.
 */
export function canvasSurfaceFor(pathname: string): CanvasSurface {
  if (pathname === '/' || pathname === '') return 'full';

  return FULL_CANVAS.some((route) => pathname === route || pathname.startsWith(`${route}/`))
    ? 'full'
    : 'tint';
}
